import { BreadcrumbJsonLd } from 'next-seo'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Head from 'next/head'
import React from 'react'

import { linkBuilder } from '@/components/Link/linkBuilder'
import { ContextValue } from '@/context/Cities'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { getBreadcrumbList } from '@/utils/breadcrumb-list'
import descriptionTranslations from '@/utils/description-translations'
import getTranslatedTitle from '@/utils/get-translated-title'
import { getAlternatePageLinksForListingDetail } from '@/utils/getAlternatePageLinks'

type Props = {
  cities: ContextValue
  listing: Listings_Detail
}

const ListingDetailHead: React.FC<Props> = ({ cities, listing }) => {
  const { publicRuntimeConfig } = getConfig()
  const { lang, t } = useTranslation()

  const translatedCityName = cities.router.city.translatedCityName

  const canonicalUrlPath = linkBuilder.listingDetail.url(lang, listing.id)
  const canonicalFullUrl = `${publicRuntimeConfig.lisaWebsite}${canonicalUrlPath}`

  // Photo, title and description
  const photoUrl = listing.photos[0]?.url

  const metaTitle = `${getTranslatedTitle(listing.detail?.titles, lang)} ${t(
    'listing-detail:title_0'
  )} ${translatedCityName} | Casavo`

  const metaDescriptions = descriptionTranslations.get(listing.descriptions, lang)
  const metaDescription = metaDescriptions.propertyDescription
    ? metaDescriptions.propertyDescription.substring(0, 250)
    : ''

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta content={metaDescription} name="description" />
        <meta content={lang} property="og:locale" />
        <meta content="article" property="og:type" />
        <meta content={metaTitle} property="og:title" />
        <meta content={metaDescription} property="og:description" />
        <meta content={`${photoUrl}?auto=compress,format&fit=clamp&ar=1:1&h=400`} property="og:image" />
        <meta content={canonicalFullUrl} property="og:url" />
        <meta content="Casavo" property="og:site_name" />
        {getAlternatePageLinksForListingDetail(lang, listing.id)}
        <link href={canonicalFullUrl} rel="canonical" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content={metaDescription} name="twitter:description" />
        <meta content={metaTitle} name="twitter:title" />
      </Head>
      <BreadcrumbJsonLd
        itemListElements={getBreadcrumbList({
          cities: cities.list,
          city: cities.router.city,
          depth: 'listingDetail',
          language: lang,
          property: listing,
          t: t,
        })}
      />
    </>
  )
}

export default ListingDetailHead
